import { useEffect, useMemo, useState } from 'react';

import clsx from 'clsx';
import { TabPanel, TabView } from 'primereact/tabview';

import { FreshStrawActionPanel } from './components/FreshStrawActionPanel';
import type { FreshStrawRow } from './components/FreshStrawActionPanel/payload';
import { FreshStrawEmbryoPanel } from './components/FreshStrawEmbryoPanel';
import { FreshStrawFilterPanel } from './components/FreshStrawFilterPanel';
import { FreshStrawTripBanner } from './components/FreshStrawTripBanner';
import { FreshStrawTripMetaPanel } from './components/FreshStrawTripMetaPanel';
import { FreshStrawFertEmbryosTab } from './components/tabs/FreshStrawFertEmbryosTab';
import { FreshStrawFlushTab } from './components/tabs/FreshStrawFlushTab';
import { FreshStrawFrozenTab } from './components/tabs/FreshStrawFrozenTab';
import { FreshStrawOnFarmTogTab } from './components/tabs/FreshStrawOnFarmTogTab';
import { FreshStrawOutsideTab } from './components/tabs/FreshStrawOutsideTab';
import { FreshStrawTransferredTab } from './components/tabs/FreshStrawTransferredTab';
import { FreshStrawUploadTab } from './components/tabs/FreshStrawUploadTab';
import { FreshStrawViewImplantsTab } from './components/tabs/FreshStrawViewImplantsTab';
import { useFreshStrawSession } from './hooks/useFreshStrawSession';
import { useFreshStrawTripContext } from './hooks/useFreshStrawTripContext';

type TabKey =
  | 'freshImplants'
  | 'frozenImplants'
  | 'viewImplants'
  | 'upload'
  | 'frozen'
  | 'freshShipments'
  | 'flush'
  | 'onFarmTog'
  | 'fertEmbryos'
  | 'outside'
  | 'transferred';

interface TabDef {
  key: TabKey;
  label: string;
}

const LAB_TABS: TabDef[] = [
  { key: 'freshImplants', label: 'Fresh Implants' },
  { key: 'frozenImplants', label: 'Frozen Implants' },
  { key: 'viewImplants', label: 'View Implants' },
  { key: 'upload', label: 'Upload Transfer/Preg Check' },
];

const FARM_TABS: TabDef[] = [
  { key: 'frozen', label: 'Frozen Embryos' },
  { key: 'freshShipments', label: 'Fresh Shipments' },
  { key: 'flush', label: 'Flushed Embryos' },
  { key: 'onFarmTog', label: 'On Farm TOG Embryos' },
  { key: 'fertEmbryos', label: 'Fert Embryos' },
  { key: 'outside', label: 'Outside Embryos' },
  { key: 'transferred', label: 'Transferred Embryos' },
];

export function FreshStrawPage() {
  const session = useFreshStrawSession();
  const [selectedRows, setSelectedRows] = useState<FreshStrawRow[]>([]);
  const [rowMathOk, setRowMathOk] = useState(true);

  const tripContext = useFreshStrawTripContext(session.tripId, session.stopId);

  const tabs = session.isTripScoped ? FARM_TABS : LAB_TABS;
  const activeKey: TabKey =
    (tabs.find((t) => t.key === session.activeTab)?.key as TabKey) ??
    tabs[0].key;
  const tabViewClassName = clsx(
    'fresh-straw-tabs flex min-h-0 flex-1 flex-col',
    'tab-view-shading',
  );
  const activeIndex = useMemo(
    () => tabs.findIndex((t) => t.key === activeKey),
    [tabs, activeKey],
  );

  useEffect(() => {
    if (!session.isTripScoped) return;
    if (tripContext.transferDateFromStop && !session.transferDate) {
      session.setTransferDate(tripContext.transferDateFromStop);
    }
    if (tripContext.resolvedSiteId && !session.transferSite) {
      session.setTransferSite(tripContext.resolvedSiteId);
    }
    if (tripContext.resolvedAccountId && !session.accountId) {
      session.setAccountId(tripContext.resolvedAccountId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    session.isTripScoped,
    tripContext.transferDateFromStop,
    tripContext.resolvedSiteId,
    tripContext.resolvedAccountId,
  ]);

  const renderTabContent = (key: TabKey) => {
    switch (key) {
      case 'freshImplants':
      case 'freshShipments':
        return (
          <div className="flex min-h-0 flex-1 flex-col gap-3 lg:flex-row">
            <FreshStrawEmbryoPanel
              session={session}
              selectedRows={selectedRows}
              onSelectionChange={setSelectedRows}
              rowMathOk={rowMathOk}
              onRowMathChange={setRowMathOk}
              className="lg:flex-1"
            />
            <FreshStrawActionPanel
              rows={selectedRows}
              session={{
                transferDate: session.transferDate,
                transferSite: session.transferSite,
              }}
              rowMathOk={rowMathOk}
              onSaved={() => setSelectedRows([])}
              className="lg:w-80"
            />
          </div>
        );
      case 'frozenImplants':
      case 'frozen':
        return <FreshStrawFrozenTab session={session} />;
      case 'viewImplants':
        return <FreshStrawViewImplantsTab session={session} />;
      case 'upload':
        return <FreshStrawUploadTab session={session} />;
      case 'flush':
        return <FreshStrawFlushTab session={session} />;
      case 'onFarmTog':
        return <FreshStrawOnFarmTogTab session={session} />;
      case 'fertEmbryos':
        return <FreshStrawFertEmbryosTab session={session} />;
      case 'outside':
        return <FreshStrawOutsideTab session={session} />;
      case 'transferred':
        return <FreshStrawTransferredTab session={session} />;
      default:
        return null;
    }
  };

  return (
    <div className="flex h-full flex-col gap-3 p-4">
      {session.isTripScoped && (
        <FreshStrawTripBanner
          context={tripContext}
          tripId={session.tripId}
          stopId={session.stopId}
        />
      )}
      {session.isTripScoped && Number(session.stopId) > 0 && (
        <FreshStrawTripMetaPanel stopId={Number(session.stopId)} />
      )}
      <FreshStrawFilterPanel session={session} />
      <TabView
        activeIndex={activeIndex >= 0 ? activeIndex : 0}
        onTabChange={(e) => {
          const next = tabs[e.index];
          if (next) session.setActiveTab(next.key);
        }}
        className={tabViewClassName}
        panelContainerClassName="flex min-h-0 flex-1 flex-col"
      >
        {tabs.map((tab) => (
          <TabPanel
            key={tab.key}
            header={tab.label}
            contentClassName="flex min-h-0 flex-1 flex-col"
          >
            {renderTabContent(tab.key)}
          </TabPanel>
        ))}
      </TabView>
    </div>
  );
}

export default FreshStrawPage;
